'use client';

import { useRef } from 'react';
import PhotoSize from '@alltrails/types/PhotoSize';
import { getPhotoUrl } from '@alltrails/shared/utils/requests/photoRequests';
import PhotoSlash from '@alltrails/denali/icons/PhotoSlash';
import { apiKey } from '@alltrails/networking/constants/apiKey';
import logPhotoCarouselSwiped from '@alltrails/amplitude/events/logPhotoCarouselSwiped';
import PageType from '@alltrails/amplitude/enums/PageType';
import SwiperCarousel from '@alltrails/denali/components/SwiperCarousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useIntl } from 'react-intl';
import { useLightbox } from '../Lightbox/LightboxContext';
import PhotoHeaderGrid from '../PhotoHeaderGrid/PhotoHeaderGrid';
import styles from './PhotoHeader.module.scss';
import LocationPhotoCard from './LocationPhotoCard';
import { PhotoHash } from '@/types/shared';
import getLocationPhotoTitle from '@/utils/getLocationPhotoTitle';

type Props = {
  photos: PhotoHash[];
  locationDetails: any;
  id?: number;
  pageType?: PageType;
};

const PhotoHeader = ({ photos, locationDetails, id, pageType }: Props) => {
  const intl = useIntl();
  const { openLightbox } = useLightbox();
  const swiper = useRef(false);
  const { type, locationSlug, name, city, state, country } = locationDetails;
  const getImageUrl = (id: number, photoHash: string) => {
    if (id && photoHash) {
      return getPhotoUrl({ id, photoHash }, PhotoSize.LargerWide, true);
    }
    return `/api/alltrails/photos/${id}/image?size=large&key=${apiKey}`;
  };
  const isMobile = useIsMobileSizedScreen();
  const shouldRenderPhotoGrid = pageType !== 'poi page' && !isMobile && photos.length > 2;
  if (photos.length === 0 && !isMobile) {
    return <div className={styles.emptyPhoto} data-testid="empty-photo" />;
  }

  return (
    <div className={styles.photoHeaderContainer}>
      {shouldRenderPhotoGrid ? (
        <PhotoHeaderGrid photos={photos} locationDetails={locationDetails} pageType={pageType} />
      ) : photos.length > 0 ? (
        <SwiperCarousel
          className={styles.carouselContainer}
          uniqueId="locations-photos-carousel"
          items={photos}
          hasPaginationGradient
          renderItem={(photo, photoIndex) => (
            <LocationPhotoCard
              photoCount={photos.length}
              photoId={(photo as PhotoHash).id}
              key={(photo as PhotoHash).id}
              index={photoIndex}
              url={getImageUrl((photo as PhotoHash).id, (photo as PhotoHash).photoHash)}
              openLightbox={openLightbox}
              locationSlug={locationSlug}
              locationDetails={{ type, city, name, state, country }}
              pageType={pageType}
              photoTitle={getLocationPhotoTitle(photo as PhotoHash, locationDetails, intl)}
            />
          )}
          getItemKey={(photo: any) => String(photo.id)}
          showPaginationDots={photos.length > 1}
          hideSlideOverflow
          arrowStyle="subtle-with-standard-arrows"
          scrollMode={photos.length < 2}
          onSlideChange={() => {
            if (!swiper.current && pageType && id) {
              logPhotoCarouselSwiped({ carousel_item_id: String(id), page_type: pageType, photo_carousel_type: 'hero image' });
              swiper.current = true;
            }
          }}
        />
      ) : (
        <div className={styles.noImage}>
          <PhotoSlash color="--color-text-secondary" size="lg" />
        </div>
      )}
    </div>
  );
};

export default PhotoHeader;
